import { Box, useMediaQuery, useTheme } from "@mui/material";
import mobileBg from "Assets/Images/Images/mobile-bg.png";
import {
  BOTTOM_MENU_HEIGHT,
  BottomMenu,
} from "Components/Layout/Menu/BottomMenu";
import { PageTitle } from "Components/Shared/PageTitles/PageTitle";
import { useHasBackgroundImage } from "Hooks/Layout/useHasBackgroundImage";
import { useIsMobile } from "Hooks/useIsMobile";
import type { BackNavigationConfig } from "Navigation/types";
import { Resources, useResource } from "Translations/Resources";
import { HeroBackgroundContext } from "Components/Layout/HeroBackground/HeroBackgroundContext";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Menu } from "./Menu/Menu";
import { SUPPORT_CONFIG } from "Infrastructure/Utils/SupportUtils";
import { UserHeader } from "Components/Layout/UserHeader/UserHeader";

/** The hero background's height when no page registers a HeroSection (staff-free dashboards, keypad). */
const HERO_BACKGROUND_MIN_HEIGHT = 400;
/** How far the dark background runs past the hero content: half the mobile dashboard gap. */
const HERO_BACKGROUND_OVERHANG = 4;

const StyledWrapper = styled(Box)<{
  $isPaddingDisabled?: boolean;
  $isMobile?: boolean;
  $isBottomMenuHidden?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${({ theme, $isPaddingDisabled, $isMobile, $isBottomMenuHidden }) =>
    $isPaddingDisabled
      ? 0
      : theme.spacing(
          6,
          3,
          SUPPORT_CONFIG.IS_ENABLED && !$isBottomMenuHidden
            ? $isMobile
              ? 17.5
              : 21
            : 6,
          3,
        )};
  z-index: 2;
`;

const StyledChildrenWrapper = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  width: 100%;
`;

type Props = {
  title: string;
  children: ReactNode;
  isTitleHiddenBelowLgScreen?: boolean;
  isPaddingDisabled?: boolean;
  onChevronClick?: () => void;
  backNavigation?: BackNavigationConfig;
  isBottomMenuHidden?: boolean;
  isTitleSmall?: boolean;
  titleBadge?: ReactNode;
};

export const AuthenticatedLayout = ({
  title,
  children,
  onChevronClick,
  isPaddingDisabled,
  isTitleHiddenBelowLgScreen,
  backNavigation = { mode: "disabled" },
  isBottomMenuHidden = false,
  isTitleSmall = false,
  titleBadge,
}: Props) => {
  const { t } = useResource();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const { hasBackgroundImage, isUserHeaderRoute } = useHasBackgroundImage();
  // Dashboard without the background image (staff roles): the header goes into the page flow.
  const isPlainMobileHeaderVisible =
    isMobile && isUserHeaderRoute && !hasBackgroundImage;

  useEffect(() => {
    document.title = `${title} | ${t(Resources.Common.AppName)}`;
  }, [title, t]);

  // The dark hero background follows the page's HeroSection: its bottom edge, measured against
  // the background box itself (the image is absolutely positioned inside the pull-to-refresh
  // wrapper, so document coordinates would be off by that wrapper's offset).
  const heroBackgroundRef = useRef<HTMLDivElement>(null);
  const [heroElement, setHeroElement] = useState<HTMLElement | null>(null);
  const [heroBottom, setHeroBottom] = useState<number | null>(null);
  const heroBackgroundContext = useMemo(() => ({ setHeroElement }), []);

  useEffect(() => {
    if (!heroElement) {
      setHeroBottom(null);
      return;
    }

    const measure = () => {
      const backgroundTop =
        heroBackgroundRef.current?.getBoundingClientRect().top ?? 0;
      setHeroBottom(heroElement.getBoundingClientRect().bottom - backgroundTop);
    };

    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(heroElement);
    window.addEventListener("resize", measure);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [heroElement]);

  const heroBackgroundHeight =
    heroBottom === null
      ? HERO_BACKGROUND_MIN_HEIGHT
      : Math.max(
          HERO_BACKGROUND_MIN_HEIGHT,
          heroBottom + HERO_BACKGROUND_OVERHANG,
        );

  const isTitleVisible = isTitleHiddenBelowLgScreen ? isLargeScreen : true;

  return (
    <HeroBackgroundContext.Provider value={heroBackgroundContext}>
      {hasBackgroundImage && (
        <Box
          ref={heroBackgroundRef}
          sx={{
            zIndex: "1",
            position: "absolute",
            top: 0,
            width: "100%",
            minWidth: "350px",
            height: `${heroBackgroundHeight}px`,
          }}
        >
          <img
            src={mobileBg}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
            }}
            alt="Top background"
          />
        </Box>
      )}
      <Box
        overflow="hidden"
        display="flex"
        position="relative"
        paddingBottom={
          isMobile && !isBottomMenuHidden ? BOTTOM_MENU_HEIGHT : undefined
        }
      >
        {hasBackgroundImage && isMobile && (
          <Box
            sx={{
              position: "absolute",
              top: 20,
              px: 4,
              zIndex: 10,
              width: "100%",
            }}
          >
            <UserHeader />
          </Box>
        )}
        {!isMobile && (
          <Menu
            isVisible
            title={title}
            onChevronClick={onChevronClick}
            backNavigation={backNavigation}
          />
        )}
        <StyledWrapper
          marginTop={hasBackgroundImage ? 30 : 0}
          $isPaddingDisabled={isPaddingDisabled}
          $isMobile={isMobile}
          $isBottomMenuHidden={isBottomMenuHidden}
        >
          {isPlainMobileHeaderVisible && (
            <Box mb={4}>
              <UserHeader />
            </Box>
          )}
          {isTitleVisible && (
            <Box
              mb={isTitleSmall ? 2 : 6}
              display="flex"
              alignItems="center"
              gap={2}
            >
              <PageTitle
                isSmall={isTitleSmall}
                title={title}
                onChevronClick={onChevronClick}
                backNavigation={backNavigation}
              />
              {titleBadge}
            </Box>
          )}
          <StyledChildrenWrapper>{children}</StyledChildrenWrapper>
        </StyledWrapper>
      </Box>
      {isMobile && !isBottomMenuHidden && <BottomMenu />}
    </HeroBackgroundContext.Provider>
  );
};
